import { useCallback, useEffect, useState } from 'react'
import { Hook } from '~/hooks/Hook'
import { classifySubjectId, matchesSubjectId, SubjectId } from '~/utils/string'
import { listLocalRecordings } from './recordingStorage'
import { prioritizeWith, sortBy } from '~/utils/array'
import { usePubSubSubscribe } from '~/pubSub/usePubSubSubscribe'
import { useWebRecorderPubSubSubscribe } from './useWebRecorderPubSubSubscribe'
import * as WebRecorder2PubSub from './pubSub'
import { pushEventTo } from '~/utils/liveView'

export type UnsubmittedRecording = SubjectId & {
  durationMs: number
  startedAtUnixMs?: number
  name: string
}

type ListMeetingsResponse = { [id: string]: { id: string; topic: string | null; startedAtUnixMs: number | null } }

const untitledMeetingName = 'Untitled'

export function useUnsubmittedRecordings(hook: Hook, { canUseWebRecorder }: { canUseWebRecorder: boolean }) {
  const [localRecordings, setLocalRecordings] = useState<UnsubmittedRecording[]>([])

  useWebRecorderPubSubSubscribe(
    'is-recordable',
    async ({ subjectId }: { subjectId: string }) => {
      if (!canUseWebRecorder) return

      const alreadyKnown = localRecordings.some((recording) => matchesSubjectId(recording, subjectId))

      const baseRecording: UnsubmittedRecording = {
        ...classifySubjectId(subjectId),
        durationMs: 0,
        startedAtUnixMs: Date.now(),
        name: untitledMeetingName,
      }

      setLocalRecordings((prev) => sortRecordings(updateKnownDurations(dedupeBySubject([...prev, baseRecording]))))

      if (!alreadyKnown) {
        debugLog(`Adding subject ${subjectId} to Live Notetaker sidebar`)

        const idToMeetingMetadata = await pushEventTo<ListMeetingsResponse>(hook, 'list_meetings', { ids: [subjectId] })
        const meetingMetadata = idToMeetingMetadata[subjectId]

        if (meetingMetadata) {
          const updatedRecording: UnsubmittedRecording = {
            ...baseRecording,
            startedAtUnixMs: meetingMetadata?.startedAtUnixMs ?? baseRecording.startedAtUnixMs,
            name: meetingMetadata?.topic ?? baseRecording.name,
          }

          setLocalRecordings((prev) =>
            prev.map((recording) => (matchesSubjectId(recording, subjectId) ? updatedRecording : recording))
          )
        }
      }

      WebRecorder2PubSub.publish('focus-subject', { subjectId })
    },
    [localRecordings, setLocalRecordings]
  )

  useWebRecorderPubSubSubscribe('recording-cleared', ({ subjectId }: { subjectId: string }) => {
    setLocalRecordings((prev) =>
      prev.map((recording) => {
        return matchesSubjectId(recording, subjectId) ? { ...recording, durationMs: 0 } : recording
      })
    )
  })

  usePubSubSubscribe('meeting-topic-updated', ({ meetingId, topic }: { meetingId: string; topic: string }) => {
    if (!canUseWebRecorder) return

    setLocalRecordings((prev) =>
      prev.map((recording) => (matchesSubjectId(recording, meetingId) ? { ...recording, name: topic } : recording))
    )
  })

  useEffect(() => {
    if (!canUseWebRecorder) {
      debugLog('Not displaying unsubmitted recordings because Web Recorder is disabled for this account')
      return
    }

    const unsubmittedRecordings = listLocalRecordings().filter((recording) => !recording.submitted)
    debugLog(`Found ${unsubmittedRecordings.length} unsubmitted recordings`)

    const ids = unsubmittedRecordings.map((recording) =>
      'meetingId' in recording ? recording.meetingId : recording.intRefId
    )

    if (ids.length > 0) {
      hook.pushEventTo(hook.el, 'list_meetings', { ids }, (idToMeetingMetadata) => {
        const localRecordingData = unsubmittedRecordings.map((recording) => {
          const meetingMetadata =
            idToMeetingMetadata['meetingId' in recording ? recording.meetingId : recording.intRefId]

          return {
            ...recording,
            durationMs: recording.durationMs,
            startedAtUnixMs: recording.startedAtUnixMs ?? meetingMetadata?.startedAtUnixMs,
            name: meetingMetadata?.topic ?? untitledMeetingName,
          }
        })

        // Per Parker, we shouldn't show recordings that were created more than a week before
        // the Live Notetaker widget was released.
        const earliestDateCutoffMs = new Date('2025-02-03').getTime()

        const newEnoughLocalRecordings = localRecordingData.filter(
          ({ startedAtUnixMs }) => startedAtUnixMs && startedAtUnixMs > earliestDateCutoffMs
        )

        debugLog(`${newEnoughLocalRecordings.length} unsubmitted recordings are new enough to display`)

        setLocalRecordings((prev) => {
          const knownLocalRecordingsFirst = sortRecordings(dedupeBySubject([...newEnoughLocalRecordings, ...prev]))
          debugLog(`Displaying ${knownLocalRecordingsFirst.length} local recordings`)
          return knownLocalRecordingsFirst
        })
      })
    }
  }, [])

  const removeRecording = useCallback((subjectId: SubjectId) => {
    debugLog(`Removing recording from sidebar`, subjectId)

    setLocalRecordings((prev) =>
      prev.filter((recording) =>
        'meetingId' in subjectId
          ? !('meetingId' in recording) || recording.meetingId !== subjectId.meetingId
          : !('intRefId' in recording) || recording.intRefId !== subjectId.intRefId
      )
    )
  }, [])

  const removeZeroLengthRecordings = useCallback((exemptSubjectIds: string[]) => {
    setLocalRecordings((prev) => {
      const filtered = prev.filter(
        (recording) =>
          recording.durationMs > 0 ||
          exemptSubjectIds.some((exemptSubjectId) => matchesSubjectId(recording, exemptSubjectId))
      )

      if (filtered.length < prev.length) {
        debugLog(
          `Removed ${prev.length - filtered.length} zero-length recordings from sidebar, exempting`,
          exemptSubjectIds
        )
      }

      return filtered
    })
  }, [])

  return canUseWebRecorder
    ? { localRecordings, removeRecording, removeZeroLengthRecordings }
    : { localRecordings: [], removeRecording: () => {}, removeZeroLengthRecordings: () => {} }
}

function sortRecordings(recordings: UnsubmittedRecording[]) {
  return prioritizeWith(
    sortBy(recordings, ({ startedAtUnixMs, durationMs }) => startedAtUnixMs ?? -durationMs),
    ({ startedAtUnixMs }) => !!startedAtUnixMs
  )
}

function updateKnownDurations(recordings: UnsubmittedRecording[]) {
  const recordingDurations = listLocalRecordings().reduce(
    (acc, recording) => {
      const subject = 'meetingId' in recording ? recording.meetingId : recording.intRefId
      acc[subject] = recording.durationMs
      return acc
    },
    {} as Record<string, number>
  )

  return recordings.map((recording) => {
    const subject = 'meetingId' in recording ? recording.meetingId : recording.intRefId
    const durationMs = recordingDurations[subject] ?? recording.durationMs
    return { ...recording, durationMs }
  })
}

// If multiple recordings have the same subject, keeps the first one only.
function dedupeBySubject(recordings: UnsubmittedRecording[]) {
  return recordings.reduce(
    ({ recordings, knownSubjects }, recording) => {
      const subject = 'meetingId' in recording ? recording.meetingId : recording.intRefId

      if (!knownSubjects.has(subject)) {
        recordings.push(recording)
        knownSubjects.add(subject)
      }
      return { recordings, knownSubjects }
    },
    { recordings: [] as UnsubmittedRecording[], knownSubjects: new Set<string>() }
  ).recordings
}

function debugLog(...args: any[]) {
  /* eslint-disable-next-line no-console */
  console.log('[useUnsubmittedRecordings]', ...args)
}
